/**
 * Web chat tool-card for the Nova lifecycle actions (M4.3): both approval-gated:
 *
 * - novaActivate → approve + activate an automation.
 * - novaRunNow   → kick off an out-of-schedule run.
 *
 * Pending → {@link ApprovalBanner} (localized confirm copy). Completed → a
 * confirmation with the automation name. Web-only; a `.native.tsx` sibling
 * renders iOS/Android. Copy from `nova.card.*`.
 */
import React, { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { CardShell } from './card-shell';
import { ApprovalBanner } from './approval-banner';
import { safeGet } from './types';
import type { ToolCardProps } from './types';

export const NovaActionCard: FC<ToolCardProps> = ({
  toolName,
  args,
  result,
  stage,
  approvalId,
}) => {
  const { t } = useTranslation('common');
  const isRunNow = toolName === 'novaRunNow';

  const name =
    safeGet<string>(result, 'automation', 'name') ??
    safeGet<string>(result, 'name') ??
    safeGet<string>(args, 'name') ??
    '';
  const title = isRunNow ? t('nova.card.runNowTitle') : t('nova.card.activateTitle');
  const confirmCopy = isRunNow ? t('nova.card.confirmRunNow') : t('nova.card.confirmActivate');

  const header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <MaterialSymbol name="conversion_path" size={15} style={{ color: '#5e29ff' }} />
      <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-tertiary)' }}>
        {title}
      </span>
    </div>
  );

  if (stage === 'pending_approval' && approvalId) {
    return (
      <CardShell>
        <div style={{ padding: '16px 18px 4px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {header}
          {name ? (
            <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)' }} dir="auto">
              {name}
            </div>
          ) : null}
        </div>
        <ApprovalBanner approvalId={approvalId} toolName={toolName} description={confirmCopy} />
      </CardShell>
    );
  }

  return (
    <CardShell>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <MaterialSymbol name="check_circle" size={16} style={{ color: 'var(--success, #1a7f37)' }} />
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-tertiary)' }}>
            {isRunNow ? t('nova.card.ranNow') : t('nova.card.activated')}
          </span>
        </div>
        {name ? (
          <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)' }} dir="auto">
            {name}
          </div>
        ) : null}
      </div>
    </CardShell>
  );
};
